import React, { useEffect, useState, createContext, useContext } from 'react';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { getCompanies } from '../utils/companyManagement';
export type UserRole = 'admin' | 'employee';
export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  company?: string;
  branch?: string;
  employeeId?: string;
}
interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
// Admin user (hardcoded)
const ADMIN_USER = {
  username: 'admin',
  password: 'admin123',
  user: {
    id: 'admin-1',
    name: 'Admin User',
    email: 'admin@lacochera.com',
    role: 'admin' as const
  }
};
export function AuthProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<User | null>(() => {
    // Restore session from sessionStorage (only for current tab)
    const savedUser = sessionStorage.getItem('criket_user_session');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  // Save user to sessionStorage whenever it changes
  useEffect(() => {
    if (user) {
      sessionStorage.setItem('criket_user_session', JSON.stringify(user));
    } else {
      sessionStorage.removeItem('criket_user_session');
    }
  }, [user]);
  const login = async (username: string, password: string): Promise<boolean> => {
    console.log('🔐 Login attempt for:', username);
    // Check if admin
    if (username === ADMIN_USER.username && password === ADMIN_USER.password) {
      console.log('✅ Admin login successful');
      setUser(ADMIN_USER.user);
      return true;
    }
    // Check if Supabase is configured
    if (!isSupabaseConfigured()) {
      console.error('❌ Supabase not configured - cannot authenticate employees');
      return false;
    }
    const companies = getCompanies();
    // Check Supabase for employee
    try {
      console.log('🔍 Checking Supabase for employee...');
      const employees = await supabaseClient.getEmployees();
      const employee = employees.find((e: any) => e.username === username && e.password === password && e.status === 'Active');
      if (employee) {
        console.log('✅ Employee found in Supabase:', employee.name);
        // Get company and branch names from IDs
        const company = companies.find(c => c.id === employee.company_id);
        const branch = company?.branches.find(b => b.id === employee.branch_id);
        const employeeUser: User = {
          id: employee.id,
          name: employee.name,
          email: employee.email,
          role: 'employee',
          company: company?.name || 'Sin empresa',
          branch: branch?.name || 'Sin sucursal',
          employeeId: employee.id
        };
        setUser(employeeUser);
        return true;
      }
      console.log('❌ Employee not found in Supabase');
      return false;
    } catch (error) {
      console.error('❌ Error checking Supabase:', error);
      return false;
    }
  };
  const logout = () => {
    setUser(null);
  };
  return <AuthContext.Provider value={{
    user,
    login,
    logout,
    isAuthenticated: !!user
  }} data-id="element-676">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}